import { z } from "zod";

import { SCENE_IDS } from "../scenes/ids";

// Widget schema — discriminated union by `type`.
// Each widget owns its own `props` shape; the renderer dispatches to the
// matching React component via the registry in src/deal-room/widgets/registry.ts.
//
// Adding a new widget type:
//   1. Add a schema variant below
//   2. Add the variant to widgetSchema's union
//   3. Register a renderer in widgets/registry.ts

// ─── Standard widgets ────────────────────────────────────────────────

export const richTextWidget = z.object({
    type: z.literal("rich_text"),
    id: z.string(),
    props: z.object({
        markdown: z.string(),
    }),
});

export const contentCardWidget = z.object({
    type: z.literal("content_card"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        body: z.string().optional(),
        href: z.string().url().optional(),
        cta: z.string().optional(),
    }),
});

export const loomEmbedWidget = z.object({
    type: z.literal("loom_embed"),
    id: z.string(),
    props: z.object({
        url: z.string().url(),
        caption: z.string().optional(),
    }),
});

export const calendlyEmbedWidget = z.object({
    type: z.literal("calendly_embed"),
    id: z.string(),
    props: z.object({
        url: z.string().url(),
        height: z.number().int().positive().optional(),
    }),
});

export const pdfEmbedWidget = z.object({
    type: z.literal("pdf_embed"),
    id: z.string(),
    props: z.object({
        // Absolute URL or root-relative path. /public assets are served from
        // root, so /<file>.pdf is the canonical form. Matches pdf_grid which
        // also accepts root-relative paths.
        url: z.string().min(1),
        title: z.string().optional(),
        height: z.number().int().positive().optional(),
    }),
});

export const nextStepWidget = z.object({
    type: z.literal("next_step"),
    id: z.string(),
    props: z.object({
        // Card heading; defaults to "Up next" when omitted.
        title: z.string().optional(),
        // Short label of the planned action ("Capabilities walkthrough", "Pricing review").
        subject: z.string(),
        // Activity flavour (drives the icon). Matches Pipedrive activity types.
        kind: z.enum(["call", "meeting", "email", "task", "deadline", "lunch", "other"]).optional(),
        // ISO date (YYYY-MM-DD). Display formatted; absence means "no date set".
        dueDate: z.string().optional(),
        // Free-text note from the Bison side — what we plan to do/ask.
        note: z.string().optional(),
        // Bison-side owner of the action (e.g. "Sean Smith").
        ownerName: z.string().optional(),
    }),
});

export const pdfGridWidget = z.object({
    type: z.literal("pdf_grid"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        description: z.string().optional(),
        // 2 = wider cards, 4 = compact thumbnails. Defaults to len(items) up to 4.
        columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
        items: z
            .array(
                z.object({
                    title: z.string(),
                    description: z.string().optional(),
                    // Absolute or root-relative path; pdf served from /public is fine.
                    url: z.string().min(1),
                    // Optional pre-rendered thumbnail. Falls back to a styled cover badge.
                    thumbnailUrl: z.string().optional(),
                }),
            )
            .min(1),
    }),
});

export const youtubeEmbedWidget = z.object({
    type: z.literal("youtube_embed"),
    id: z.string(),
    props: z.object({
        url: z.string().url(),
        caption: z.string().optional(),
    }),
});

export const gdocEmbedWidget = z.object({
    type: z.literal("gdoc_embed"),
    id: z.string(),
    props: z.object({
        // Accepts any Google Docs URL form (/edit, /view, /preview).
        // The widget normalizes to /edit?usp=sharing&rm=embedded for the iframe
        // (inline editable when the doc is shared "anyone with link can edit"
        // and the viewer is signed into a Google account).
        url: z.string().url(),
        title: z.string().optional(),
        height: z.number().int().positive().optional(),
        // Helper text rendered above the iframe alongside a copy-link button.
        // Use to instruct supplier-side viewers ("send this link to other team
        // members to complete the form together"). Omitting hides the helper
        // strip; the iframe still renders.
        helperText: z.string().optional(),
    }),
});

export const wistiaEmbedWidget = z.object({
    type: z.literal("wistia_embed"),
    id: z.string(),
    props: z.object({
        // Wistia URL forms accepted: full embed URL, share URL, or just the
        // hashed video id. The widget normalizes to fast.wistia.net/embed/iframe/{id}.
        url: z.string(),
        caption: z.string().optional(),
    }),
});

export const blogPostsWidget = z.object({
    type: z.literal("blog_posts"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        description: z.string().optional(),
        posts: z.array(
            z.object({
                title: z.string(),
                excerpt: z.string().optional(),
                url: z.string().url(),
                imageUrl: z.string().url().optional(),
                date: z.string().optional(), // ISO 8601 or human-readable
                author: z.string().optional(),
                category: z.string().optional(),
            }),
        ),
    }),
});

export const faqWidget = z.object({
    type: z.literal("faq"),
    id: z.string(),
    props: z.object({
        // Optional since 2026-09-04: on the FAQ page the H1 is the heading.
        title: z.string().optional(),
        description: z.string().optional(),
        // Iconography (2026-09-05): icon names are basenames under
        // public/brand/icons/faq/; each item may carry one.
        faqs: z.array(
            z.object({
                // Optional group heading (2026-09-04). Consecutive items with
                // the same section render under one heading; items without
                // one render in an unheaded block, so legacy stored FAQs
                // (no section) keep their flat list.
                section: z.string().optional(),
                icon: z.string().optional(),
                question: z.string(),
                answer: z.string(),
            }),
        ),
    }),
});

// Per-signer recipient state, as reflected from DocuSign Connect. Used in
// widget.data.recipients[] to render a "who has signed" sub-block.
const docusignRecipient = z.object({
    email: z.string(),
    name: z.string(),
    role: z.string().optional(),
    status: z.enum(["created", "sent", "delivered", "completed", "declined"]),
    signedAt: z.string().datetime().optional(),
});

// Headline envelope status. `pending` is the placeholder bucket — envelope not
// yet created in DocuSign. Everything else corresponds 1:1 with a DocuSign
// envelope state. `deleted` covers soft-deleted drafts.
const docusignStatus = z.enum([
    "pending",
    "sent",
    "delivered",
    "completed",
    "declined",
    "voided",
    "deleted",
]);

// Enrichment block populated by /api/webhooks/docusign or by the refresh
// wrapper. When present, renderers show timestamps + per-signer progress on
// top of the base status. Shared by `docusign_embed` and `agreements`.
const docusignEnvelopeState = z.object({
    envelopeId: z.string(),
    status: z.enum(["sent", "delivered", "completed", "declined", "voided", "deleted"]),
    sentAt: z.string().datetime().optional(),
    deliveredAt: z.string().datetime().optional(),
    signedAt: z.string().datetime().optional(),
    voidedAt: z.string().datetime().optional(),
    voidReason: z.string().optional(),
    recipients: z.array(docusignRecipient).default([]),
    generatedAt: z.string().datetime(),
});

export const docusignEmbedWidget = z.object({
    type: z.literal("docusign_embed"),
    id: z.string(),
    props: z.object({
        // Document identity (e.g., "Contract", "MAP Addendum"). When set, takes
        // top of the card and the status-based copy becomes the secondary line.
        title: z.string().optional(),
        envelopeId: z.string(),
        signingUrl: z.string().url().optional(),
        // Status reflected from DocuSign webhook. v0.5: read-only display.
        status: docusignStatus,
    }),
    // Absent → renderer falls back to props-only display.
    data: docusignEnvelopeState.nullable().optional(),
});

// Agreements page: ONE list of agreements, each a row in the document list
// whose reading pane shows the DRAFT (a PDF anyone on the room can open)
// until its DocuSign envelope exists, then the SIGNATURE face (status, signer
// progress, the signed copy once pulled). Replaces the older "pdf_grid#agreement-drafts on top, separate
// docusign_embed cards at the bottom" split, which asked the reader to match
// two lists by title. (2026-09-03)
//
// The swap rule, and the reason it is not a status check: a card shows the
// signature face when `envelopeId` is a real DocuSign envelope GUID (see
// src/deal-room/envelope-id.ts). Provisioner placeholders like
// "hush-crib-sor-pending" keep the draft face, so a pre-send room never shows
// a supplier a fake envelope id or an empty signing state.
//
// Ownership stays split the same way it always was:
//   /deal-room-contract   writes items[].{id,title,description,draftUrl}
//   /refresh-deal-room-docusign  writes items[].{envelopeId,status,signingUrl,envelope}
// Neither skill removes the other's fields.
export const agreementsWidget = z.object({
    type: z.literal("agreements"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        description: z.string().optional(),
        items: z
            .array(
                z.object({
                    // Stable per-agreement key, e.g. "seller-of-record". Used by
                    // the refresh skill to find the row to update.
                    id: z.string(),
                    title: z.string(),
                    description: z.string().optional(),
                    // The drafted PDF. Root-relative room-upload path is the
                    // normal form: /api/rooms/<slug>/uploads/<file>.pdf
                    draftUrl: z.string().min(1).optional(),
                    // Set once an envelope exists. A non-GUID value is treated
                    // as a placeholder and keeps the draft face.
                    envelopeId: z.string().optional(),
                    signingUrl: z.string().url().optional(),
                    status: docusignStatus.optional(),
                    envelope: docusignEnvelopeState.nullable().optional(),
                    // The completed, countersigned PDF, pulled from DocuSign
                    // into the room's Agreements folder once the envelope is
                    // completed (same root-relative form as draftUrl). Owned
                    // by /refresh-deal-room-docusign. Absent until pulled.
                    signedUrl: z.string().min(1).optional(),
                }),
            )
            .min(1),
    }),
});

const customerLogo = z.object({
    // Stable identifier used by `customerOverrides` to hide a
    // logo for a specific room (e.g. when the supplier is a
    // direct competitor of a featured customer). Optional for
    // backwards compat with rooms provisioned before canonical
    // hydration landed; the canonical layer always sets it.
    id: z.string().optional(),
    name: z.string(),
    logoUrl: z.string().url().optional(),
    /** Override the navy default for the styled-wordmark fallback. */
    brandColor: z.string().optional(),
    /** The brand's website; the tile becomes a link when set
     *  (2026-09-04). Comes from the logo library's `website`. */
    href: z.string().url().optional(),
});

export const customerLogosWidget = z.object({
    type: z.literal("customer_logos"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        description: z.string().optional(),
        customers: z.array(customerLogo),
        // Per-room curation. The SM hides a logo per supplier here without
        // touching the canonical customer list. The hydrator strips this
        // field from the API output so renderers only see the resolved
        // customers[] array.
        customerOverrides: z
            .array(
                z.object({
                    id: z.string(),
                    hidden: z.boolean().optional(),
                }),
            )
            .optional(),
    }),
});

export const customerTestimonialsWidget = z.object({
    type: z.literal("customer_testimonials"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        description: z.string().optional(),
        testimonials: z.array(
            z.object({
                // Stable identifier used by `testimonialOverrides` to hide
                // a testimonial for a specific room. Optional for backwards
                // compat; the canonical layer always sets it.
                id: z.string().optional(),
                quote: z.string(),
                authorName: z.string(),
                authorRole: z.string().optional(),
                companyName: z.string(),
                companyLogoUrl: z.string().url().optional(),
                authorPhotoUrl: z.string().url().optional(),
            }),
        ),
        // Per-room curation. Same shape as customerOverrides.
        testimonialOverrides: z
            .array(
                z.object({
                    id: z.string(),
                    hidden: z.boolean().optional(),
                }),
            )
            .optional(),
    }),
});

export const marketplaceGridWidget = z.object({
    type: z.literal("marketplace_grid"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        description: z.string().optional(),
        marketplaces: z.array(
            z.object({
                name: z.string(),
                logoUrl: z.string().url().optional(),
                /** The marketplace's own site; the logo opens it in a new tab. */
                href: z.string().url().optional(),
                /** Height multiplier for the mark (0.85 on square badges so
                 *  area, not height, reads equal across the strip). */
                logoScale: z.number().positive().optional(),
                /** Optional override for the styled-wordmark fallback color. */
                brandColor: z.string().optional(),
            }),
        ),
        // Bison-owned storefronts relevant to THIS supplier (Bison Baby for
        // a nursery brand, Bison Furniture for furniture, ...). Rendered as
        // a second band under the marketplace list. Per-room: the canonical
        // widget carries the static marketplace list and no dtc; the
        // provisioning skill sets dtc from the supplier's category.
        dtc: z
            .object({
                title: z.string(),
                description: z.string().optional(),
                sites: z.array(
                    z.object({
                        id: z.string(),
                        name: z.string(),
                        /** Root-relative (/brand/logos/sub-brands/...) or absolute. */
                        logoUrl: z.string(),
                        href: z.string().url().optional(),
                        blurb: z.string().optional(),
                        logoScale: z.number().positive().optional(),
                    }),
                ),
            })
            .optional(),
    }),
});

// The capabilities tour: sections of website-style feature rows, each row
// copy + one of the vendored site scenes (src/deal-room/scenes). Content is
// canonical (templates/capabilities-canonical.ts); rooms normally inherit it
// untouched.
export const featureRowsWidget = z.object({
    type: z.literal("feature_rows"),
    id: z.string(),
    props: z.object({
        /** Heading over the tab strip ("How we do it"). */
        title: z.string().optional(),
        description: z.string().optional(),
        sections: z.array(
            z.object({
                id: z.string(),
                eyebrow: z.string().optional(),
                /** Section heading; absent on a page whose H1 already names the
                 *  page (How We Partner, 2026-09-08). */
                title: z.string().optional(),
                intro: z.string().optional(),
                /** Rows align their copy with the top of the scene instead of its middle
                 *  (the exclusivity pitch room, Elijah 2026-10-27). Off by default. */
                alignTop: z.boolean().optional(),
                /** Draw the between-sections rule and spacing above this section even
                 *  when it is the first, to part it from the widget above (2026-10-27). */
                divider: z.boolean().optional(),
                /** Icon key for the Capabilities column, resolved to a MUI outlined
                 *  glyph in CapabilitiesOverview's RAIL_ICONS (the names are the
                 *  website's icons-nucleo basenames, kept so data never moved). */
                icon: z.string().optional(),
                rows: z.array(
                    z.object({
                        id: z.string(),
                        title: z.string(),
                        /** Markdown (paragraphs and links; relative hrefs are room pages). */
                        body: z.string(),
                        /** Absent = a text-only row at the reading measure (2026-09-08). */
                        scene: z.enum(SCENE_IDS).optional(),
                        /** A static photo in place of a scene, as the site's photo rows
                         *  (public/images/what-we-do/**, copied by sync-scenes.mjs). */
                        image: z.object({ src: z.string(), alt: z.string() }).optional(),
                        /** Several stills cycled as a crossfade slideshow (the supplier's own
                         *  PDP screenshots on How We Partner, per room via page data
                         *  `rowMedia`; Bill, 2026-09-08). Wins over `scene` and `image`. */
                        images: z
                            .array(z.object({ src: z.string(), alt: z.string() }))
                            .min(1)
                            .optional(),
                        /** Which side the scene sits on; defaults to alternating. */
                        mediaSide: z.enum(["left", "right"]).optional(),
                        /** Scene box width as a share of the 600px cap (0.8 = 20% smaller);
                         *  Bill sizes rows so the scenes read consistent (2026-09-08). */
                        sceneScale: z.number().min(0.3).max(1).optional(),
                        /** Space above and below the scene, in px, so a short wide scene
                         *  sits with room around it (exclusivity pitch room, 2026-10-27). */
                        scenePadY: z.number().min(0).max(120).optional(),
                        /** A logo wall in place of a scene (exclusive brands on the
                         *  exclusivity pitch room; Elijah, 2026-10-29). */
                        logos: z.array(customerLogo).min(1).optional(),
                    }),
                ),
            }),
        ),
    }),
});

// One vendored site scene on its own, capped wide and centred, with optional
// noun labels over it (percent of the scene box) and a caption under it. The
// How We Partner hero (2026-09-08).
export const sceneWidget = z.object({
    type: z.literal("scene"),
    id: z.string(),
    props: z.object({
        scene: z.enum(SCENE_IDS),
        maxWidth: z.number().optional(),
        labels: z.array(z.object({ text: z.string(), x: z.number(), y: z.number() })).optional(),
        caption: z.string().optional(),
    }),
});

// Company-scale figures (the website's About-page stat band): a big figure
// over a short label, one of them highlighted in orange. Static canonical
// copy, no snapshot date and no source note; live per-supplier numbers are
// metric_tiles. Shipped 2026-09-07 (Bill) for the Capabilities page.
export const statBandWidget = z.object({
    type: z.literal("stat_band"),
    id: z.string(),
    props: z.object({
        items: z
            .array(
                z.object({
                    value: z.string(),
                    label: z.string(),
                    highlight: z.boolean().optional(),
                    /** Root-relative brand icon (public/brand/icons/**), shown on a card. */
                    icon: z.string().optional(),
                }),
            )
            .min(1),
        /** Each item as an outlined card with its icon (the exclusivity pitch room,
         *  Elijah 2026-10-27). Off by default: the open figure row stays the band. */
        cards: z.boolean().optional(),
    }),
});

// Rollout timeline: one row per workstream, bars placed by calendar date on
// a month axis, coloured by owner (`series`, chartPalette in order: Blue,
// Green, Orange). A bar whose start equals its end is a milestone. Shipped
// 2026-09-28 (Bill) for the Tvilum pitch room's plan page.
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const ganttWidget = z.object({
    type: z.literal("gantt"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        caption: z.string().optional(),
        // Where the dates come from. Required: no unsourced timelines.
        source: z.string(),
        // The axis, inclusive.
        start: isoDate,
        end: isoDate,
        // "day" labels each day Day 1, Day 2... from start (a short onboarding plan);
        // the default bands by month.
        axis: z.enum(["month", "day"]).optional(),
        // Legend labels; a bar's `series` indexes this list.
        series: z.array(z.string()).min(1),
        rows: z
            .array(
                z.object({
                    label: z.string(),
                    bars: z
                        .array(
                            z.object({
                                label: z.string(),
                                start: isoDate,
                                end: isoDate,
                                series: z.number().int().nonnegative(),
                            }),
                        )
                        .min(1),
                }),
            )
            .min(1),
    }),
});

// The leadership team as the website's About page lists it: a square
// headshot, the name and the role (the capability the person leads).
// Canonical copy on the Capabilities page's "Who we are" panel (2026-09-08).
export const teamGridWidget = z.object({
    type: z.literal("team_grid"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        intro: z.string().optional(),
        members: z
            .array(
                z.object({
                    name: z.string(),
                    role: z.string(),
                    /** Root-relative (/brand/team/...) or absolute. */
                    photoUrl: z.string(),
                }),
            )
            .min(1),
    }),
});

// Mutual Action Plan. Renamed from map_read_only — v0.5 makes it interactive
// (any room user can check tasks off; state persists via /api/rooms/[slug]/state).
// The fixture status field is the *initial* state; user toggles override it
// at the room level. v1 will move state from the sidecar JSON file to Postgres
// without changing this schema.
export const mapWidget = z.object({
    type: z.literal("map"),
    id: z.string(),
    props: z.object({
        // Per-room visibility / phase overrides applied to MAP tasks by
        // the canonical hydrator (see canonical/hydrate.ts). Tasks are
        // targeted by stable `taskId`; positional state keys are migrated
        // away from at the same time, so reordering or hiding tasks no
        // longer invalidates stored status.
        //
        // Resolution: for each entry with a matching `taskId` in any
        // phases[].tasks[], hidden=true drops the task from the rendered
        // output. The hydrator strips this field from the API response so
        // renderers only ever see the resolved phases[].tasks[].
        taskOverrides: z
            .array(
                z.object({
                    taskId: z.string(),
                    hidden: z.boolean().optional(),
                }),
            )
            .optional(),
        phases: z.array(
            z.object({
                name: z.string(),
                /**
                 * Staff-only phase ("Bison Internal", 2026-11-02): always last,
                 * collapsed at rest, left out of the chevrons and the current
                 * phase; the API strips it for suppliers
                 * (canonical/internal-steps.ts).
                 */
                internal: z.boolean().optional(),
                tasks: z.array(
                    z.object({
                        // Stable identifier used by state keys and per-room
                        // taskOverrides. Optional for backwards compat with
                        // rooms provisioned before the move from positional
                        // (`<widgetId>:<phaseIdx>:<taskIdx>`) to stable
                        // (`<widgetId>:<taskId>`) keys. New rooms always set it.
                        taskId: z.string().optional(),
                        title: z.string(),
                        /**
                         * Markdown body shown under the title. Standard inline syntax —
                         * including links. Internal page links use `page:<slug>` as the
                         * href and render as in-room SPA links (e.g.
                         * `[partnership tab](page:partnership)`).
                         */
                        description: z.string().optional(),
                        kind: z.enum(["meeting", "task", "milestone"]),
                        status: z.enum(["pending", "in_progress", "complete"]).optional(),
                        /** Who owns the next move on this task. */
                        assignee: z.enum(["bison", "supplier", "shared"]).optional(),
                        /**
                         * Free-form due-date string. ISO 8601 (2026-05-12) is preferred —
                         * the renderer formats valid ISO into a short locale string.
                         * Anything else (e.g. "Day 7", "TBD") passes through unchanged.
                         */
                        dueDate: z.string().optional(),
                        /**
                         * When true, the task renders an inline upload control. Files
                         * POST to /api/rooms/{slug}/uploads (the same endpoint backing
                         * the Uploads page), so they automatically appear on the
                         * Uploads tab. Per-task filenames also persist into room state
                         * so the task shows what's been uploaded against it.
                         */
                        acceptsUpload: z.boolean().optional(),
                        /**
                         * Pre-attached, Bison-supplied downloadable files (templates,
                         * authorization letters, reference PDFs). Rendered as chips
                         * below the description. `url` is absolute or root-relative
                         * (typically /program-docs/onboarding/<file>).
                         */
                        attachments: z
                            .array(
                                z.object({
                                    name: z.string(),
                                    url: z.string().min(1),
                                }),
                            )
                            .optional(),
                        /**
                         * Web pages the step points at (the supplier portal, its user
                         * guide). Rendered as pills under "Links" that open in a new
                         * tab, so the step copy itself stays link-free (2026-10-27).
                         * `page:<slug>[?query]` points inside the room, such as a
                         * Documents folder (`page:documents?folder=insurance`).
                         */
                        links: z
                            .array(
                                z.object({
                                    name: z.string(),
                                    url: z.union([z.string().url(), z.string().regex(/^page:[a-z0-9-]+(\?.*)?$/)]),
                                }),
                            )
                            .optional(),
                        /**
                         * Requirements the step opens as a popup in the Programs
                         * modal's style: a pill under "Requirements", then title,
                         * summary, markdown body (GFM tables), PDFs in the paginated
                         * viewer, then `after` markdown below them (2026-10-30,
                         * Elijah; trial on benzara's COI step).
                         */
                        guide: z
                            .object({
                                label: z.string(),
                                title: z.string(),
                                summary: z.string().optional(),
                                body: z.string(),
                                documents: z
                                    .array(z.object({ title: z.string(), url: z.string().min(1) }))
                                    .optional(),
                                after: z.string().optional(),
                            })
                            .optional(),
                    }),
                ),
            }),
        ),
    }),
});

// Interactive fulfillment-network map (Tvilum pitch, 2026-09-24): the room's
// order points, the supplier's warehouse today, optimal node presets, candidate
// metros and each 3PL provider's facilities. The reader builds a network by
// preset, by provider or by clicking facilities, and reads the share of orders
// within 300 and 600 great-circle miles of the nearest node. All data stored on
// the widget (behind room sign-in); nothing is fetched. Ported from the
// center-of-gravity report in the hub.
const networkNode = z.object({ lat: z.number(), lon: z.number(), name: z.string() });
export const networkMapWidget = z.object({
    type: z.literal("network_map"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        caption: z.string().optional(),
        // Where the points came from. Required: no unsourced maps.
        source: z.string(),
        // What one point counts ("orders", "cartons"). Default "orders".
        unit: z.string().optional(),
    }),
    data: z.object({
        // [lat, lon, count] per aggregated destination.
        points: z.array(z.tuple([z.number(), z.number(), z.number()])).min(1),
        total: z.number(),
        baseline: z.array(networkNode).min(1),
        centerOfMass: networkNode.optional(),
        metros: z.array(networkNode),
        // "k2" / "k3" / "k4": the optimal free-placement networks.
        presets: z.record(z.string(), z.array(networkNode)),
        providers: z.array(
            z.object({
                id: z.string(),
                name: z.string(),
                facilities: z.array(networkNode).min(1),
                // Best facility pairs / triples for this provider on these points.
                best: z
                    .record(
                        z.string(),
                        z.object({
                            nodes: z.array(networkNode).min(1),
                            within600: z.number(),
                            avgMiles: z.number(),
                        }),
                    )
                    .optional(),
                recommended: z.boolean().optional(),
                note: z.string().optional(),
            }),
        ),
        // Amazon's Seller Fulfilled Prime delivery-speed target for the item's
        // size tier: the share of Prime page views that must show a two-day
        // promise. The widget reads reach within 600 miles as the two-day
        // proxy and shows met / short against this figure.
        sfp: z
            .object({
                tier: z.string(),
                twoDayRequiredPct: z.number(),
                fiveDayRequiredPct: z.number().optional(),
                source: z.string(),
            })
            .optional(),
        generatedAt: z.string(),
    }),
});

// Several tables behind one tab strip on the design system's table (the
// assortment tiers on the Tvilum pitch room, 2026-09-24). Cells are
// pre-formatted strings; the provisioner owns number formatting.
export const tabbedTableWidget = z.object({
    type: z.literal("tabbed_table"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        tabs: z
            .array(
                z.object({
                    label: z.string(),
                    count: z.number().int().optional(),
                    caption: z.string().optional(),
                    columns: z
                        .array(
                            z.object({
                                label: z.string(),
                                align: z.enum(["left", "right"]).optional(),
                            }),
                        )
                        .min(1),
                    rows: z.array(z.array(z.string())),
                    source: z.string().optional(),
                }),
            )
            .min(1),
    }),
});

// Standalone hero / inline image. Renders a centered <img> with optional
// caption. Used when a single composed graphic communicates better than a
// generated grid of items (e.g. marketplace logos pre-laid-out by design).
export const imageWidget = z.object({
    type: z.literal("image"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        src: z.string().min(1),
        alt: z.string(),
        caption: z.string().optional(),
        maxWidth: z.number().int().positive().optional(),
    }),
});

// Expandable list of program offerings. Each item collapses to a title +
// optional one-line summary; expanding reveals a markdown body. Used on the
// capabilities page to layer program detail behind a click without dominating
// the layout.
export const programAccordionWidget = z.object({
    type: z.literal("program_accordion"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        description: z.string().optional(),
        programs: z
            .array(
                z.object({
                    title: z.string(),
                    summary: z.string().optional(),
                    body: z.string(), // markdown
                }),
            )
            .min(1),
    }),
});

// Programs page explorer. Carries the full program catalog as a flat list,
// each program tagged with the tier it sits in (Foundation, Growth, Scale,
// Strategic). The component renders the four tiers as stacked bands, Strategic
// on top, exactly like the B2C diagram on the Notion Programs & Playbooks
// page: one tile per program with a checkbox (the supplier enables the
// program; state persists via /api/rooms/{slug}/state programStatus) and a
// two-tone icon. Clicking a tile opens a modal with the program write-up and
// its documents. The roadmap / outcome views and the hero pyramid were
// retired on 2026-09-05 (Bill).
export const programsExplorerWidget = z.object({
    type: z.literal("programs_explorer"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        description: z.string().optional(),
        // Optional copy rendered below the pyramid hero, above the view toggle.
        heroIntro: z.string().optional(),
        programs: z
            .array(
                z.object({
                    // Stable identifier used by `programOverrides` to target a
                    // specific program for hide/phase/recommend tweaks per room.
                    // Optional for backwards compat with rooms provisioned before
                    // canonical hydration landed; the canonical layer always sets it.
                    id: z.string().optional(),
                    title: z.string(),
                    summary: z.string().optional(),
                    body: z.string(), // markdown
                    tier: z.enum(["foundation", "growth", "scale", "strategic"]),
                    // Icon file name under /program-icons/ (a Nucleo SVG copied
                    // verbatim from the hub design-system library, already in
                    // the navy + orange two-tone). Rendered bottom-right of the
                    // tile, matching the Programs & Playbooks diagram in Notion.
                    icon: z.string().optional(),
                    // Documents shown inside the program modal (brochures,
                    // decks). `url` is a room-relative or absolute PDF URL.
                    documents: z.array(z.object({ title: z.string(), url: z.string() })).optional(),
                    // Legacy fields from the retired roadmap / outcome views.
                    // Rooms stored before 2026-09-05 still carry them; the
                    // renderer ignores both.
                    phase: z.enum(["day_one", "build", "grow", "strategic"]).optional(),
                    outcomes: z.array(z.string()).optional(),
                    // SM-controlled spotlight. Renders a "Recommended" pill next
                    // to the program title and bubbles it to the top of each
                    // group. recommendedNote (optional) shows as a tooltip.
                    recommended: z.boolean().optional(),
                    recommendedNote: z.string().optional(),
                    // Canonical-level hide. Unlike the per-room `programOverrides`
                    // hide (which an SM sets for one supplier), this drops the
                    // program from EVERY room's Programs page — the renderer
                    // filters it out. Used to retire a program from the default
                    // catalogue while keeping its content in place ("may come back
                    // later"). A room can still surface a hidden program by
                    // overriding it in programOverrides if needed.
                    hidden: z.boolean().optional(),
                }),
            )
            .min(1),
        // Per-room overrides applied AFTER the canonical programs list is
        // merged in (see canonical/hydrate.ts). The SM uses these to hide
        // programs that don't apply to this supplier, move a program to a
        // different phase, or flag a program as Recommended — without
        // duplicating the canonical programs array into the stored room JSON.
        //
        // Override resolution: for each entry with a matching `id` in
        // programs[], hidden=true drops the program; phase/recommended/
        // recommendedNote replace the canonical value for that field. The
        // hydrator strips this field from the API output so renderers only
        // ever see the resolved `programs` array.
        programOverrides: z
            .array(
                z.object({
                    id: z.string(),
                    hidden: z.boolean().optional(),
                    phase: z.enum(["day_one", "build", "grow", "strategic"]).optional(),
                    recommended: z.boolean().optional(),
                    recommendedNote: z.string().optional(),
                }),
            )
            .optional(),
        // Legacy (retired outcome view). Ignored by the renderer; kept so
        // rooms stored before 2026-09-05 still validate.
        outcomes: z
            .array(
                z.object({
                    label: z.string(),
                    description: z.string().optional(),
                }),
            )
            .optional(),
        // Legacy (retired view toggle). Ignored by the renderer.
        defaultView: z.enum(["roadmap", "outcomes"]).optional(),
    }),
});

// ─── Live-data widgets ───────────────────────────────────────────────
// Each carries a pre-rendered snapshot in `data` (computed at provision time).
// At v1 the same component swaps `data` for a fetched payload — the prop
// shape is identical. This is migration contract #5.

export const opportunitySnapshotWidget = z.object({
    type: z.literal("opportunity_snapshot"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
    }),
    data: z.object({
        asins: z.array(
            z.object({
                asin: z.string(),
                title: z.string(),
                bsr: z.number().int().nullable(),
                buyBoxWinRate30d: z.number().min(0).max(1).nullable(),
                modeledMonthlyRevenue: z.number().nullable(),
            }),
        ),
        totalModeledRevenue: z.number(),
        generatedAt: z.string(), // ISO 8601
    }),
});

export const suppressionDiagnosesWidget = z.object({
    type: z.literal("suppression_diagnoses"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
    }),
    data: z.object({
        items: z.array(
            z.object({
                asin: z.string(),
                title: z.string(),
                reason: z.string(),
                correctiveAction: z.string(),
            }),
        ),
        generatedAt: z.string(),
    }),
});

export const channelCoverageWidget = z.object({
    type: z.literal("channel_coverage"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
    }),
    data: z.object({
        channels: z.array(
            z.object({
                name: z.string(), // "Amazon", "Walmart", etc.
                bisonActive: z.boolean(),
                supplierActive: z.boolean(),
                note: z.string().optional(),
                // What we actually saw when we looked (added 2026-09-03).
                // `supplierActive: false` on its own is a claim of absence, and
                // absence needs evidence — a search that returned nothing is a
                // different fact from a site that blocked the check. Rooms
                // provisioned before this field render exactly as before.
                observedStatus: z
                    .enum(["listed", "not_found_in_search", "not_observable"])
                    .optional(),
                // One line naming what was queried and what came back.
                evidence: z.string().optional(),
                checkedAt: z.string().optional(), // ISO 8601
            }),
        ),
        generatedAt: z.string(),
    }),
});

// Opportunity-page KPI strip. A row of stat tiles, any of which can carry a
// gauge (share, coverage, a 0-100 quality score) instead of a bare number.
//
// The null contract is the point of this widget: `value: null` is a first-class
// state that renders "Not available" plus `sourceNote` — the tool or system
// that would fill it. A figure we could not source is never quietly dropped and
// never guessed at; it shows as a named gap. Every tile carries `source`.
export const metricTilesWidget = z.object({
    type: z.literal("metric_tiles"),
    id: z.string(),
    props: z.object({
        /** Tiles per row on desktop (default 3); 4 for a four-tile headline row. */
        columns: z.number().int().min(1).max(4).optional(),
        title: z.string().optional(),
        description: z.string().optional(),
        /** Hide the "Snapshot · date" line and the per-tile source lines on a pitch
         *  page (exclusivity room, Elijah 2026-10-27). Sources stay required below. */
        hideSnapshot: z.boolean().optional(),
        hideSources: z.boolean().optional(),
    }),
    data: z.object({
        tiles: z
            .array(
                z.object({
                    label: z.string(),
                    // Pre-formatted for display (the provisioner owns currency
                    // and thousands separators). null = not available.
                    value: z.string().nullable(),
                    caption: z.string().optional(),
                    // Renders an @mui/x-charts Gauge instead of a plain figure.
                    gauge: z
                        .object({
                            value: z.number(),
                            max: z.number().positive(),
                            valueLabel: z.string().optional(),
                        })
                        .optional(),
                    // Where the number came from. Required — no unsourced values.
                    source: z.string(),
                    // Colours the tile label from chartPalette (index); never a border.
                    accent: z.number().int().min(0).max(11).optional(),
                    // What would fill it when value is null.
                    sourceNote: z.string().optional(),
                }),
            )
            .min(1),
        generatedAt: z.string(),
    }),
});

// Opportunity-page competitive benchmark. Puts the supplier's own listings
// against the category set we observed: a price-band distribution chart, a
// review-count chart, and the competitor table behind both.
export const competitorBenchmarkWidget = z.object({
    type: z.literal("competitor_benchmark"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        description: z.string().optional(),
    }),
    data: z.object({
        // The supplier's own position, drawn on the charts as the marker line.
        subject: z.object({
            name: z.string(),
            price: z.number().nullable(),
            reviews: z.number().int().nullable(),
            rating: z.number().nullable(),
            note: z.string().optional(),
        }),
        competitors: z.array(
            z.object({
                brand: z.string(),
                title: z.string(),
                asin: z.string().optional(),
                price: z.number().nullable(),
                reviews: z.number().int().nullable(),
                rating: z.number().nullable(),
                monthlyRevenue: z.number().nullable().optional(),
            }),
        ),
        // Price distribution of the observed set. Provisioner computes the
        // buckets so the widget never has to guess a bucketing rule.
        priceBands: z
            .array(
                z.object({
                    label: z.string(), // "$100-199"
                    count: z.number().int().nonnegative(),
                    containsSubject: z.boolean().optional(),
                }),
            )
            .optional(),
        // How the set was assembled and what it is (and is not).
        sampleNote: z.string().optional(),
        source: z.string(),
        generatedAt: z.string(),
    }),
});

export const pricingCompetitivenessWidget = z.object({
    type: z.literal("pricing_competitiveness"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
    }),
    data: z.object({
        items: z.array(
            z.object({
                asin: z.string(),
                title: z.string(),
                bisonPrice: z.number().nullable(),
                lowestCompetitorPrice: z.number().nullable(),
                pricePosition: z.enum(["lowest", "competitive", "above_market"]).nullable(),
            }),
        ),
        generatedAt: z.string(),
    }),
});

// Parts of a whole as a donut (exclusivity case study, Elijah 2026-10-27).
// Values must add up to a whole and be non-negative; slices take chartPalette in order.
export const donutChartWidget = z.object({
    type: z.literal("donut_chart"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        caption: z.string().optional(),
        format: z.enum(["currency", "number"]).optional(),
        // Where the numbers came from. Required — no unsourced charts.
        source: z.string(),
        // Hide the source line on a pitch page (exclusivity room, Elijah 2026-10-27).
        hideSource: z.boolean().optional(),
    }),
    data: z.object({
        // One to three donuts side by side, each with its own heading, an optional
        // figure in the hole and an optional note under its legend.
        donuts: z
            .array(
                z.object({
                    title: z.string(),
                    center: z.object({ value: z.string(), label: z.string().optional() }).optional(),
                    note: z.string().optional(),
                    slices: z
                        .array(z.object({ label: z.string(), value: z.number().nonnegative() }))
                        .min(2)
                        .max(6),
                }),
            )
            .min(1)
            .max(3),
        generatedAt: z.string(),
    }),
});

// Opportunity-page single-series bar chart. Shipped 2026-09-08 (Bill): the page
// went visual-first, and share of voice, prices by channel, a ground rate by
// zone and a before/after all want the same shape. One series, a label per
// bar, an optional highlight for the supplier's own bar. Colours come from
// chartPalette; the highlight is palette[0], the rest palette[5].
export const barChartWidget = z.object({
    type: z.literal("bar_chart"),
    id: z.string(),
    props: z.object({
        title: z.string(),
        caption: z.string().optional(),
        layout: z.enum(["horizontal", "vertical"]).optional(),
        // How to print a value on the bar and the axis.
        format: z.enum(["currency", "number", "percent"]).optional(),
        // Where the numbers came from. Required — no unsourced charts.
        source: z.string(),
        // Appended to the axis label of a highlighted bar. Default "(you)".
        highlightSuffix: z.string().optional(),
        // Opt-in extras (exclusivity case study, Elijah 2026-10-27). Axis titles; short
        // figures on the bars ("416K") and axis ("$400K"); a trailing average line over
        // N bars; a shaded band from the bar whose label starts with shadeFrom to the
        // end; a dotted line from the first bar to the last labelled with the change.
        xLabel: z.string().optional(),
        yLabel: z.string().optional(),
        compact: z.boolean().optional(),
        rollingAverage: z.number().int().min(2).optional(),
        rollingLabel: z.string().optional(),
        // The bars' name in the legend shown with a trailing average (default: title).
        valueLabel: z.string().optional(),
        shadeFrom: z.string().optional(),
        shadeLabel: z.string().optional(),
        showChange: z.boolean().optional(),
        // Colour each bar by its group, in chartPalette order, with a legend above
        // (exclusivity room, Elijah 2026-10-27: orders by SKU coloured Head/Core/Tail).
        groups: z.array(z.object({ key: z.string(), label: z.string() })).max(6).optional(),
        // Hide the category labels under the bars (many bars, names in the tooltip).
        hideCategoryLabels: z.boolean().optional(),
        // No card border or padding: the chart sits straight on the page.
        plain: z.boolean().optional(),
        // No gridlines behind the bars (exclusivity room forecast chart, 2026-10-27).
        hideGrid: z.boolean().optional(),
    }),
    data: z.object({
        bars: z
            .array(
                z.object({
                    label: z.string(),
                    value: z.number(),
                    highlight: z.boolean().optional(),
                    // Key of the bar's entry in props.groups.
                    group: z.string().optional(),
                    // Stacked bar: the value split by props.groups key (a histogram of items
                    // by order volume, each bucket split Head/Core/Tail).
                    parts: z.record(z.string(), z.number()).optional(),
                }),
            )
            .min(1),
        generatedAt: z.string(),
    }),
});

// Design-system callout (info / warning / danger; never success). Replaces
// paragraphs of letter text on the Opportunity page (Bill, 2026-09-08).
// Who does what, one panel per party with areas as labelled bullet groups, and an
// optional full-width strip for what both sides do together (exclusivity pitch
// room, Elijah 2026-10-27).
export const responsibilitiesWidget = z.object({
    type: z.literal("responsibilities"),
    id: z.string(),
    props: z.object({
        parties: z
            .array(
                z.object({
                    name: z.string(),
                    // The party's logo, drawn above its name (Elijah, 2026-10-27).
                    logoUrl: z.string().min(1).optional(),
                    // Logo height in px (default 40), to balance two marks of different weight.
                    logoHeight: z.number().min(16).max(80).optional(),
                    summary: z.string().optional(),
                    groups: z.array(z.object({ title: z.string(), items: z.array(z.string()).min(1) })).min(1),
                }),
            )
            .min(1)
            .max(3),
        // A row of cards, each an optional icon centred over its text.
        together: z
            .object({
                title: z.string(),
                items: z.array(z.union([z.string(), z.object({ text: z.string(), icon: z.string().optional() })])).min(1).max(4),
            })
            .optional(),
    }),
});

// The metrics a partnership is measured on: groups of cards, each a metric's name,
// what it measures and which direction is good (exclusivity Metrics page, 2026-10-27).
export const metricDefinitionsWidget = z.object({
    type: z.literal("metric_definitions"),
    id: z.string(),
    props: z.object({
        groups: z
            .array(
                z.object({
                    title: z.string(),
                    metrics: z
                        .array(
                            z.object({
                                name: z.string(),
                                description: z.string(),
                                // A design-system icon drawn above the name.
                                icon: z.string().optional(),
                                // "higher" / "lower", or a short phrase of its own.
                                better: z.string().optional(),
                            }),
                        )
                        .min(1),
                }),
            )
            .min(1),
    }),
});

export const calloutWidget = z.object({
    type: z.literal("callout"),
    id: z.string(),
    props: z.object({
        variant: z.enum(["info", "warning", "danger"]),
        title: z.string().optional(),
        body: z.string(),
        action: z.string().optional(),
    }),
});

// Opportunity-page lever cards. One card per Pipedrive wedge on the deal:
// what we see today, what changes with Bison, and one sourced figure.
export const leverCardsWidget = z.object({
    type: z.literal("lever_cards"),
    id: z.string(),
    props: z.object({
        title: z.string().optional(),
        intro: z.string().optional(),
        /** The projection the page opens with ("3 to 5x growth on Amazon over
         *  the next six months"), Bill's stated aim per deal, rendered before
         *  the cards (2026-09-08). */
        headline: z.string().optional(),
        /** One paragraph of the challenges we heard and saw, under the headline. */
        challenges: z.string().optional(),
    }),
    data: z.object({
        levers: z
            .array(
                z.object({
                    wedge: z.string(), // the Pipedrive wedge label, shown as the eyebrow
                    title: z.string(),
                    today: z.string(),
                    withBison: z.string(),
                    stat: z.object({ value: z.string(), label: z.string() }).optional(),
                    source: z.string().optional(),
                }),
            )
            .min(1),
        generatedAt: z.string(),
    }),
});

// ─── Discriminated union ─────────────────────────────────────────────

export const widgetSchema = z.discriminatedUnion("type", [
    richTextWidget,
    contentCardWidget,
    loomEmbedWidget,
    calendlyEmbedWidget,
    pdfEmbedWidget,
    pdfGridWidget,
    nextStepWidget,
    youtubeEmbedWidget,
    wistiaEmbedWidget,
    gdocEmbedWidget,
    docusignEmbedWidget,
    agreementsWidget,
    marketplaceGridWidget,
    featureRowsWidget,
    sceneWidget,
    statBandWidget,
    ganttWidget,
    teamGridWidget,
    customerLogosWidget,
    customerTestimonialsWidget,
    blogPostsWidget,
    faqWidget,
    mapWidget,
    imageWidget,
    networkMapWidget,
    tabbedTableWidget,
    programAccordionWidget,
    programsExplorerWidget,
    opportunitySnapshotWidget,
    suppressionDiagnosesWidget,
    channelCoverageWidget,
    pricingCompetitivenessWidget,
    metricTilesWidget,
    competitorBenchmarkWidget,
    barChartWidget,
    donutChartWidget,
    leverCardsWidget,
    calloutWidget,
    responsibilitiesWidget,
    metricDefinitionsWidget,
]);

export type Widget = z.infer<typeof widgetSchema>;
export type WidgetType = Widget["type"];

// Per-type extractors for the registry (so each renderer gets the right type).
export type WidgetOfType<T extends WidgetType> = Extract<Widget, { type: T }>;
